import React, { useEffect, useState } from 'react'
import { fetchOnlineOverviewStats } from '../../api'
import { Typography, Card, CardContent, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip } from '@mui/material'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip as ChartTooltip, Legend, PieChart, Pie, Cell, AreaChart, Area } from 'recharts'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee'
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart'
import PeopleIcon from '@mui/icons-material/People'
import PaymentIcon from '@mui/icons-material/Payment'
import InventoryIcon from '@mui/icons-material/Inventory'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet'

const PIE_COLORS = ['#2563eb', '#0f766e', '#7c3aed', '#ea580c', '#dc2626', '#d97706']
const STATUS_LABELS = { '0': 'Pending', '1': 'Confirmed', '2': 'Delivered', '3': 'Cancelled', '4': 'Returned', '5': 'Processing', '6': 'Shipped' }
const STATUS_COLORS = { '0': '#d97706', '1': '#2563eb', '2': '#15803d', '3': '#dc2626', '4': '#7c3aed', '5': '#ea580c', '6': '#0f766e' }

export default function OnlineOverview() {
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState(null)

  const fmt = (num) => {
    const v = Number(num || 0)
    if (v === 0) return '₹0.00'
    const [i, d] = v.toFixed(2).split('.')
    const t = i.substring(i.length - 3)
    const o = i.substring(0, i.length - 3)
    return `₹${o ? o.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ',' : ''}${t}.${d}`
  }

  useEffect(() => {
    setLoading(true)
    fetchOnlineOverviewStats()
      .then(res => setData(res?.data || null))
      .catch(() => setData(null))
      .finally(() => setLoading(false))
  }, [])

  if (loading) return <Typography>Loading...</Typography>
  if (!data) return <Typography>No data available.</Typography>

  const sm = data.summary || {}
  const wallet = data.wallet || {}
  const monthly = data.monthlyOrders || []
  const customers = data.topCustomers || []
  const pincodes = data.topPincodes || []
  const areas = data.topAreas || []
  const products = data.topProducts || []
  const statusBd = data.statusBreakdown || []
  const paymentBd = data.paymentBreakdown || []
  const recentWallet = data.recentWalletTxns || []

  return (
    <Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 1.5, mb: 2 }}>
        {[
          { label: 'Total Orders', value: sm.totalOrders?.toLocaleString(), icon: <ShoppingCartIcon />, bg: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)', trend: 'lifetime' },
          { label: 'Total Revenue', value: fmt(sm.totalRevenue), icon: <CurrencyRupeeIcon />, bg: 'linear-gradient(135deg, #0f766e 0%, #0d9488 100%)', trend: 'all orders' },
          { label: 'Avg Order Value', value: fmt(sm.avgOrder), icon: <TrendingUpIcon />, bg: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)', trend: 'per order' },
          { label: 'Active Buyers', value: sm.activeUsers?.toLocaleString(), icon: <PeopleIcon />, bg: 'linear-gradient(135deg, #ea580c 0%, #f97316 100%)', trend: 'placed orders' },
        ].map(m => (
          <Card key={m.label} sx={{ background: m.bg, color: '#fff', borderRadius: 2, boxShadow: '0 4px 14px rgba(0,0,0,0.15)' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minHeight: 72, py: '12px !important', px: '14px !important' }}>
              <Box sx={{ display: 'grid', placeItems: 'center', width: 38, height: 38, bgcolor: 'rgba(255,255,255,0.2)', borderRadius: '10px' }}>{m.icon}</Box>
              <Box sx={{ minWidth: 0 }}><Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '.6rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.5px' }}>{m.label}</Typography><strong style={{ display: 'block', marginTop: 2, fontSize: '.95rem', fontWeight: 800 }}>{m.value}</strong></Box>
              <Box sx={{ ml: 'auto', alignSelf: 'flex-start', bgcolor: 'rgba(255,255,255,0.2)', px: 0.75, py: 0.25, borderRadius: 1, fontSize: '.55rem', fontWeight: 700 }}>{m.trend}</Box>
            </CardContent>
          </Card>
        ))}
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 1.5, mb: 2 }}>
        {[
          { label: 'Total Users', value: sm.totalUsers?.toLocaleString(), icon: <PeopleIcon />, bg: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)', trend: 'registered' },
          { label: 'Wallet Credits', value: fmt(wallet.totalCredited), icon: <AccountBalanceWalletIcon />, bg: 'linear-gradient(135deg, #15803d 0%, #16a34a 100%)', trend: `${wallet.totalTransactions} txns` },
          { label: 'Wallet Debits', value: fmt(wallet.totalDebited), icon: <AccountBalanceWalletIcon />, bg: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)', trend: 'used' },
        ].map(m => (
          <Card key={m.label} sx={{ background: m.bg, color: '#fff', borderRadius: 2, boxShadow: '0 4px 14px rgba(0,0,0,0.15)' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minHeight: 72, py: '12px !important', px: '14px !important' }}>
              <Box sx={{ display: 'grid', placeItems: 'center', width: 38, height: 38, bgcolor: 'rgba(255,255,255,0.2)', borderRadius: '10px' }}>{m.icon}</Box>
              <Box sx={{ minWidth: 0 }}><Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '.6rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.5px' }}>{m.label}</Typography><strong style={{ display: 'block', marginTop: 2, fontSize: '.95rem', fontWeight: 800 }}>{m.value}</strong></Box>
              <Box sx={{ ml: 'auto', alignSelf: 'flex-start', bgcolor: 'rgba(255,255,255,0.2)', px: 0.75, py: 0.25, borderRadius: 1, fontSize: '.55rem', fontWeight: 700 }}>{m.trend}</Box>
            </CardContent>
          </Card>
        ))}
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1, mb: 2 }}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}><Typography variant="h6">Monthly Orders & Revenue</Typography><Chip label={`${monthly.length} months`} size="small" variant="outlined" /></Box>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={monthly} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="label" tick={{ fill: '#64748b', fontSize: 11 }} />
                <YAxis yAxisId="left" tick={{ fill: '#64748b', fontSize: 11 }} />
                <YAxis yAxisId="right" orientation="right" tick={{ fill: '#64748b', fontSize: 11 }} tickFormatter={v => `₹${(v / 1000).toFixed(0)}k`} />
                <ChartTooltip formatter={(v, name) => [name === 'Revenue' ? fmt(v) : v, name]} />
                <Legend />
                <Bar yAxisId="left" dataKey="orders" fill="#2563eb" name="Orders" radius={[4, 4, 0, 0]} />
                <Bar yAxisId="right" dataKey="revenue" fill="#0f766e" name="Revenue" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}><Typography variant="h6">Order Status</Typography></Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Box sx={{ flex: '0 0 140px', height: 140 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={statusBd.map(s => ({ ...s, label: STATUS_LABELS[s.status] || s.status }))} dataKey="count" nameKey="label" cx="50%" cy="50%" outerRadius={55} innerRadius={25}>
                      {statusBd.map((_, i) => <Cell key={i} fill={STATUS_COLORS[statusBd[i].status] || PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <ChartTooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
              <Box sx={{ flex: 1 }}>
                {statusBd.map((s, i) => (
                  <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: STATUS_COLORS[s.status] || PIE_COLORS[i % PIE_COLORS.length] }} />
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>{STATUS_LABELS[s.status] || s.status}</Typography>
                    </Box>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>{s.count}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1, mb: 2 }}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}><PeopleIcon color="primary" /><Typography variant="h6">Top Customers</Typography></Box>
            <TableContainer sx={{ maxHeight: 300 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, fontSize: '.62rem' }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '.62rem' }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '.62rem' }}>Mobile</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '.62rem', textAlign: 'right' }}>Orders</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '.62rem', textAlign: 'right' }}>Spent</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {customers.map((c, i) => (
                    <TableRow key={i} hover>
                      <TableCell sx={{ fontSize: '.6rem', color: '#94a3b8' }}>{i + 1}</TableCell>
                      <TableCell sx={{ fontSize: '.62rem', fontWeight: 600 }}>{c.name || 'Guest'}</TableCell>
                      <TableCell sx={{ fontSize: '.62rem' }}>{c.mobile || '-'}</TableCell>
                      <TableCell sx={{ fontSize: '.62rem', textAlign: 'right', fontWeight: 700 }}>{c.order_count}</TableCell>
                      <TableCell sx={{ fontSize: '.62rem', textAlign: 'right', fontWeight: 700, color: '#15803d' }}>{fmt(c.total_spent)}</TableCell>
                    </TableRow>
                  ))}
                  {customers.length === 0 && <TableRow><TableCell colSpan={5} align="center"><Typography variant="caption" color="text.secondary">No data.</Typography></TableCell></TableRow>}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}><InventoryIcon color="primary" /><Typography variant="h6">Top Products</Typography></Box>
            <TableContainer sx={{ maxHeight: 300 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, fontSize: '.62rem' }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '.62rem' }}>Product</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '.62rem', textAlign: 'right' }}>Qty</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '.62rem', textAlign: 'right' }}>Revenue</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {products.map((p, i) => (
                    <TableRow key={i} hover>
                      <TableCell sx={{ fontSize: '.6rem', color: '#94a3b8' }}>{i + 1}</TableCell>
                      <TableCell sx={{ fontSize: '.62rem', fontWeight: 600 }}>{p.product_name}</TableCell>
                      <TableCell sx={{ fontSize: '.62rem', textAlign: 'right' }}>{p.total_qty}</TableCell>
                      <TableCell sx={{ fontSize: '.62rem', textAlign: 'right', fontWeight: 700, color: '#15803d' }}>{fmt(p.total_revenue)}</TableCell>
                    </TableRow>
                  ))}
                  {products.length === 0 && <TableRow><TableCell colSpan={4} align="center"><Typography variant="caption" color="text.secondary">No data.</Typography></TableCell></TableRow>}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1, mb: 2 }}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}><LocationOnIcon color="primary" /><Typography variant="h6">Top Pincodes</Typography></Box>
            <TableContainer sx={{ maxHeight: 280 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, fontSize: '.62rem' }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '.62rem' }}>Pincode</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '.62rem' }}>City</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '.62rem' }}>Area</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '.62rem', textAlign: 'right' }}>Orders</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '.62rem', textAlign: 'right' }}>Revenue</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pincodes.map((p, i) => (
                    <TableRow key={i} hover>
                      <TableCell sx={{ fontSize: '.6rem', color: '#94a3b8' }}>{i + 1}</TableCell>
                      <TableCell sx={{ fontSize: '.62rem', fontWeight: 600 }}>{p.pincode}</TableCell>
                      <TableCell sx={{ fontSize: '.62rem' }}>{p.city || '-'}</TableCell>
                      <TableCell sx={{ fontSize: '.62rem' }}>{p.area || '-'}</TableCell>
                      <TableCell sx={{ fontSize: '.62rem', textAlign: 'right', fontWeight: 700 }}>{p.order_count}</TableCell>
                      <TableCell sx={{ fontSize: '.62rem', textAlign: 'right', fontWeight: 700, color: '#15803d' }}>{fmt(p.revenue)}</TableCell>
                    </TableRow>
                  ))}
                  {pincodes.length === 0 && <TableRow><TableCell colSpan={6} align="center"><Typography variant="caption" color="text.secondary">No data.</Typography></TableCell></TableRow>}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}><LocationOnIcon sx={{ color: '#ea580c' }} /><Typography variant="h6">Top Areas</Typography></Box>
            <TableContainer sx={{ maxHeight: 280 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, fontSize: '.62rem' }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '.62rem' }}>Area</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '.62rem' }}>City</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '.62rem', textAlign: 'right' }}>Orders</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '.62rem', textAlign: 'right' }}>Revenue</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {areas.map((a, i) => (
                    <TableRow key={i} hover>
                      <TableCell sx={{ fontSize: '.6rem', color: '#94a3b8' }}>{i + 1}</TableCell>
                      <TableCell sx={{ fontSize: '.62rem', fontWeight: 600 }}>{a.area}</TableCell>
                      <TableCell sx={{ fontSize: '.62rem' }}>{a.city || '-'}</TableCell>
                      <TableCell sx={{ fontSize: '.62rem', textAlign: 'right', fontWeight: 700 }}>{a.order_count}</TableCell>
                      <TableCell sx={{ fontSize: '.62rem', textAlign: 'right', fontWeight: 700, color: '#15803d' }}>{fmt(a.revenue)}</TableCell>
                    </TableRow>
                  ))}
                  {areas.length === 0 && <TableRow><TableCell colSpan={5} align="center"><Typography variant="caption" color="text.secondary">No data.</Typography></TableCell></TableRow>}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1, mb: 2 }}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}><PaymentIcon color="primary" /><Typography variant="h6">Payment Methods</Typography></Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Box sx={{ flex: '0 0 140px', height: 140 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={paymentBd} dataKey="count" nameKey="payment_method" cx="50%" cy="50%" outerRadius={55} innerRadius={25}>
                      {paymentBd.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <ChartTooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
              <Box sx={{ flex: 1 }}>
                {paymentBd.map((p, i) => (
                  <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: PIE_COLORS[i % PIE_COLORS.length] }} />
                      <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>{p.payment_method}</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>{p.count} orders</Typography>
                      <Typography variant="caption" sx={{ color: '#15803d', fontSize: '.55rem' }}>{fmt(p.revenue)}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}><AccountBalanceWalletIcon sx={{ color: '#7c3aed' }} /><Typography variant="h6">Recent Wallet Transactions</Typography></Box>
            <TableContainer sx={{ maxHeight: 280 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, fontSize: '.62rem' }}>User</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '.62rem' }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '.62rem', textAlign: 'right' }}>Amount</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '.62rem' }}>Message</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '.62rem' }}>Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentWallet.map((t, i) => (
                    <TableRow key={i} hover>
                      <TableCell sx={{ fontSize: '.62rem', fontWeight: 600 }}>{t.name || `User #${t.user_id}`}</TableCell>
                      <TableCell sx={{ fontSize: '.62rem' }}>
                        <Chip label={t.type} size="small" sx={{ height: 16, fontSize: '.5rem', bgcolor: t.type === 'credit' ? '#dcfce7' : '#fee2e2', color: t.type === 'credit' ? '#15803d' : '#dc2626', fontWeight: 700 }} />
                      </TableCell>
                      <TableCell sx={{ fontSize: '.62rem', textAlign: 'right', fontWeight: 700, color: t.type === 'credit' ? '#15803d' : '#dc2626' }}>
                        {t.type === 'credit' ? '+' : '-'}{fmt(t.amount)}
                      </TableCell>
                      <TableCell sx={{ fontSize: '.6rem', color: '#64748b', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.message}</TableCell>
                      <TableCell sx={{ fontSize: '.6rem', color: '#94a3b8' }}>{t.transaction_date ? new Date(t.transaction_date).toLocaleDateString('en-IN') : '-'}</TableCell>
                    </TableRow>
                  ))}
                  {recentWallet.length === 0 && <TableRow><TableCell colSpan={5} align="center"><Typography variant="caption" color="text.secondary">No transactions.</Typography></TableCell></TableRow>}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Box>
    </Box>
  )
}
